import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
    ArrowLeft, 
    Zap, 
    RotateCcw,
    Check,
    Sparkles,
    Wind,
    Hand,
    Anchor,
    Activity,
    Brain,
    Settings,
    ChevronRight,
    Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { IconButton, PrimaryButton, SecondaryButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { Container } from '../components/brand/BrandLayout';
import { BoxBreathingVisual } from '../components/brand/BoxBreathingVisual';
import { ContextualActionZone } from '../components/brand/AppShell';
import { PersistentBottomBar, BottomBarSpacer } from '../components/brand/PersistentBottomBar';
import { CoRegulateMark } from '../components/Logo';
import { 
    MicroInsight, 
    GROUNDING_INSIGHTS, 
    SOMATIC_INSIGHTS,
    INTEROCEPTION_INSIGHTS,
    getRandomInsight 
} from '../components/brand/MicroInsight';
import { CountdownRing } from '../components/brand/CountdownRing';
import { ToolFeedback } from '../components/ToolFeedback';
import { QuickBilateral } from '../components/QuickBilateral';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Breathing patterns for the micro reset (subset of most useful quick patterns)
const BREATHING_PATTERNS = [
    {
        id: 'sigh',
        name: 'Physiological Sigh',
        subtitle: 'Quick Reset',
        description: 'Double inhale followed by long exhale. Instantly calms the nervous system.',
        bestFor: 'Immediate stress relief, 30 seconds',
        color: '#4DB6AC',
        glassVariant: 'glass-card-sage'
    },
    {
        id: 'box',
        name: 'Box Breathing',
        subtitle: '4-4-4-4',
        description: 'Equal parts inhale, hold, exhale, hold. Used by Navy SEALs for calm under pressure.',
        bestFor: 'Focus, anxiety, stress',
        color: '#64B5F6',
        glassVariant: 'glass-card-blue'
    },
    {
        id: '478',
        name: '4-7-8 Breathing',
        subtitle: 'Relaxing Breath',
        description: 'Extended hold and exhale activates parasympathetic response.',
        bestFor: 'Sleep, deep relaxation, panic',
        color: '#7D8E95',
        glassVariant: 'glass-card-lavender'
    },
    {
        id: 'extended',
        name: 'Extended Exhale',
        subtitle: '4-8',
        description: 'Exhale twice as long as inhale. Activates vagus nerve and calming response.',
        bestFor: 'Anxiety, racing heart, tension',
        color: '#81C784',
        glassVariant: 'glass-card-gold'
    },
    {
        id: 'coherent',
        name: 'Coherent Breathing',
        subtitle: '5-5',
        description: '5 breaths per minute. Optimizes heart rate variability and autonomic balance.',
        bestFor: 'Heart health, emotional balance',
        color: '#F48FB1',
        glassVariant: 'glass-card-rose'
    }
];

// Category icons mapping
const CATEGORY_ICONS = {
    breath: Wind,
    somatic: Hand,
    grounding: Anchor,
    movement: Activity,
    mindful: Brain,
    bilateral: Eye
};

// Category colors - 4 color palette (Teal, Coral, Dusty Rose, Neutral) distributed evenly
const CATEGORY_COLORS = {
    breath: { glass: 'glass-card', text: 'text-[#1D6B6B]', badge: 'bg-gradient-to-br from-[#2A8585] to-[#1D6B6B]' },           // Teal
    somatic: { glass: 'glass-card-rose', text: 'text-[#1D6B6B]', badge: 'bg-gradient-to-br from-[#FFBB98] to-[#E09060]' },      // Coral
    grounding: { glass: 'glass-card', text: 'text-[#1D6B6B]', badge: 'bg-gradient-to-br from-[#E8A0A0] to-[#C97B7B]' },         // Dusty Rose
    movement: { glass: 'glass-card-rose', text: 'text-[#1D6B6B]', badge: 'bg-gradient-to-br from-[#9AABB8] to-[#7D8E95]' },     // Neutral
    mindful: { glass: 'glass-card-lavender', text: 'text-[#1D6B6B]', badge: 'bg-gradient-to-br from-[#2A8585] to-[#1D6B6B]' },  // Teal
    bilateral: { glass: 'glass-card-sage', text: 'text-[#1D6B6B]', badge: 'bg-gradient-to-br from-[#E8A0A0] to-[#C97B7B]' }     // Dusty Rose
};

// Bilateral calming category definition
const BILATERAL_CATEGORY = {
    id: 'bilateral',
    name: 'EMDR/Bilateral Regulation',
    description: 'Calming sensory input to ground'
};

// Fallback actions if database is unavailable
const FALLBACK_ACTIONS = [
    { id: 'mr_palm_press', action: 'Press your palms together firmly for 5 seconds. Release slowly.', duration: 10, category: 'somatic' },
    { id: 'mr_shoulder_roll', action: 'Roll your shoulders back three times. Let them drop.', duration: 10, category: 'somatic' },
    { id: 'mr_color_find', action: 'Look at something blue in your space. Really see it.', duration: 10, category: 'grounding' },
    { id: 'mr_4_6_breath', action: 'Inhale for 4. Exhale for 6. Let the longer exhale settle you.', duration: 15, category: 'breath', isBreath: true },
    { id: 'mr_warm_hands', action: 'Rub your hands together until they feel warm. Place them on your face.', duration: 15, category: 'somatic' },
    { id: 'mr_jaw_release', action: 'Unclench your jaw. Let your tongue relax away from the roof of your mouth.', duration: 10, category: 'somatic' },
    { id: 'mr_sigh_breath', action: 'Breathe in softly. Let the exhale fall out of your mouth like a sigh.', duration: 10, category: 'breath', isBreath: true },
    { id: 'mr_feet_push', action: 'Feel your feet on the floor. Push down gently. Feel the support.', duration: 10, category: 'grounding' },
];

const MicroReset = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [currentAction, setCurrentAction] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [showInsight, setShowInsight] = useState(false);
    const [currentInsight, setCurrentInsight] = useState(null);
    const [actionCount, setActionCount] = useState(0);
    const [ringKey, setRingKey] = useState(0);
    const [sessionActions, setSessionActions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCompletion, setShowCompletion] = useState(false);
    const [showCategoryPicker, setShowCategoryPicker] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selectedFocus, setSelectedFocus] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [isPersonalized, setIsPersonalized] = useState(false);
    const [showBilateral, setShowBilateral] = useState(false);
    const [showBreathingPatterns, setShowBreathingPatterns] = useState(false);

    // Get focus from URL params (e.g., from AI Guide)
    const urlFocus = searchParams.get('focus');

    // Fetch categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch(`${API_URL}/api/tools/micro-reset/categories`);
                if (response.ok) {
                    const data = await response.json();
                    setCategories(data.categories || []);
                }
            } catch (err) {
                console.error('Failed to fetch categories:', err);
            }
        };
        fetchCategories();
    }, []);

    // Fetch personalized session
    const fetchPersonalizedSession = async (focus = null) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/tools/micro-reset/personalized-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify({
                    focus: focus || 'mixed',
                    count: 6
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.actions && data.actions.length > 0) {
                    setSessionActions(data.actions);
                    setCurrentAction(data.actions[0]);
                    setCurrentIndex(0);
                    setSessionId(data.session_id);
                    setIsPersonalized(data.personalization_applied);
                    setSelectedFocus(data.focus);
                } else {
                    // Fallback
                    setSessionActions(FALLBACK_ACTIONS);
                    setCurrentAction(FALLBACK_ACTIONS[0]);
                }
            } else {
                setSessionActions(FALLBACK_ACTIONS);
                setCurrentAction(FALLBACK_ACTIONS[0]);
            }
        } catch (err) {
            console.error('Failed to fetch personalized session:', err);
            setSessionActions(FALLBACK_ACTIONS);
            setCurrentAction(FALLBACK_ACTIONS[0]);
        } finally {
            setLoading(false);
        }
    };

    // Initialize session
    useEffect(() => {
        if (urlFocus) {
            // AI Guide or direct link specified a focus
            if (urlFocus === 'bilateral') {
                setShowBilateral(true);
                setLoading(false);
            } else if (urlFocus === 'breath') {
                setShowBreathingPatterns(true);
                setLoading(false);
            } else {
                fetchPersonalizedSession(urlFocus);
            }
        } else {
            // Show category picker first so user can choose
            setShowCategoryPicker(true);
            setLoading(false);
        }
    }, [urlFocus]);

    // Get contextual insight based on action type
    const getContextualInsight = (action) => {
        if (action?.isBreath || action?.category === 'breath') {
            return getRandomInsight(INTEROCEPTION_INSIGHTS);
        }
        const combined = [...GROUNDING_INSIGHTS, ...SOMATIC_INSIGHTS];
        return getRandomInsight(combined);
    };

    const handleNext = () => {
        setIsAnimating(true);
        const newActionCount = actionCount + 1;
        setActionCount(newActionCount);
        setRingKey(prev => prev + 1);
        
        setTimeout(() => {
            // Move to next action in the sequence
            const nextIndex = currentIndex + 1;
            
            if (nextIndex < sessionActions.length) {
                // More actions in current session
                setCurrentIndex(nextIndex);
                setCurrentAction(sessionActions[nextIndex]);
            } else {
                // End of session - fetch new personalized batch
                fetchPersonalizedSession(selectedFocus).then(() => {
                    setCurrentIndex(0);
                });
            }
            
            setIsAnimating(false);
            
            // Show insight occasionally
            if (newActionCount >= 2 && newActionCount % 2 === 0 && Math.random() > 0.4) {
                const insight = getContextualInsight(sessionActions[nextIndex] || currentAction);
                if (insight) {
                    setCurrentInsight(insight);
                    setShowInsight(true);
                }
            } else {
                setShowInsight(false);
            }
        }, 200);
    };

    const handleDismissInsight = () => {
        setShowInsight(false);
    };

    const handleDone = () => {
        // Show feedback if they did 2+ actions
        if (actionCount >= 2) {
            setShowFeedback(true);
        } else {
            setShowCompletion(true);
        }
    };

    const handleFeedbackClose = () => {
        setShowFeedback(false);
        setShowCompletion(true);
    };

    const handleCategorySelect = (categoryId) => {
        setShowCategoryPicker(false);
        setSelectedFocus(categoryId);
        
        // Special handling for bilateral category - show dedicated component
        if (categoryId === 'bilateral') {
            setShowBilateral(true);
            setLoading(false);
            return;
        }
        
        // Special handling for breath category - show breathing patterns
        if (categoryId === 'breath') {
            setShowBreathingPatterns(true);
            setLoading(false);
            return;
        }
        
        fetchPersonalizedSession(categoryId);
    };

    // Handle completing bilateral session
    const handleBilateralComplete = () => {
        setShowBilateral(false);
        setActionCount(prev => prev + 1);
        // Optionally continue with other actions or show completion
        setShowCompletion(true);
    };

    // Handle selecting a breathing pattern - navigate to full breathing tool
    const handleBreathingPatternSelect = (patternId) => {
        navigate(`/regulate/breathing?pattern=${patternId}`);
    };

    const handleReturnToRegulate = () => {
        navigate('/home?tab=regulation');
    };

    const handleReturnHome = () => {
        navigate('/home?tab=regulation');
    };

    // Get category info for current action
    const getCategoryInfo = (action) => {
        const category = action?.category || 'somatic';
        const Icon = CATEGORY_ICONS[category] || Hand;
        const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS.somatic;
        const categoryName = categories.find(c => c.id === category)?.name || category;
        return { Icon, colors, categoryName };
    };

    // Show loading state
    if (loading || (!currentAction && !showBilateral && !showBreathingPatterns && !showCategoryPicker)) {
        return (
            <div className="min-h-screen bg-transparent flex items-center justify-center">
                <div className="text-center">
                    <div className="w-3 h-3 rounded-full bg-[#7D8E95] animate-breathe mx-auto mb-4" />
                    <SmallText className="text-[#7D8E95]">
                        {loading ? 'Preparing your personalized reset...' : 'Loading...'}
                    </SmallText>
                </div>
            </div>
        );
    }

    // EMDR/Bilateral Regulation screen - simplified, dedicated experience
    if (showBilateral) {
        return (
            <div className="min-h-screen bg-transparent">
                <header className="sticky top-0 z-50 glass-header">
                    <Container size="sm" className="py-5 flex items-center justify-between">
                        <IconButton 
                            onClick={() => {
                                setShowBilateral(false);
                                setShowCategoryPicker(true);
                            }}
                            data-testid="back-btn"
                            className="text-[#7D8E95] hover:text-[#1D6B6B]"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </IconButton>
                        <Heading as="h1" size="text-lg">EMDR/Bilateral Regulation</Heading>
                        <div className="w-10" />
                    </Container>
                </header>

                <main className="max-w-xl mx-auto px-6 py-8">
                    <QuickBilateral 
                        onComplete={handleBilateralComplete}
                        onNavigateToFull={() => navigate('/regulate/emdr')}
                        duration={30}
                        showVisual={true}
                    />
                </main>
            </div>
        );
    }

    // Breathing Patterns screen - shows pattern cards like BreathingPatterns.jsx
    if (showBreathingPatterns) {
        return (
            <div className="min-h-screen bg-transparent">
                <header className="sticky top-0 z-50 glass-header">
                    <Container size="sm" className="py-5 flex items-center justify-between">
                        <IconButton 
                            onClick={() => {
                                setShowBreathingPatterns(false);
                                setShowCategoryPicker(true);
                            }}
                            data-testid="back-btn"
                            className="text-[#7D8E95] hover:text-[#1D6B6B]"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </IconButton>
                        <Heading as="h1" size="text-lg">Breathing Patterns</Heading>
                        <div className="w-10" />
                    </Container>
                </header>

                <main className="max-w-xl mx-auto px-6 py-8">
                    <div className="text-center mb-6">
                        <SmallText className="text-[#7D8E95]">
                            Each pattern has a different effect on your nervous system
                        </SmallText>
                    </div>

                    <div className="space-y-3">
                        {BREATHING_PATTERNS.map((pattern, index) => (
                            <button
                                key={pattern.id}
                                onClick={() => handleBreathingPatternSelect(pattern.id)}
                                className={`w-full ${pattern.glassVariant || 'glass-card'} rounded-2xl p-5 text-left hover:scale-[1.02] transition-all`}
                                style={{ animationDelay: `${index * 50}ms` }}
                                data-testid={`breathing-pattern-${pattern.id}`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div>
                                        <Heading level={4} className="text-[#1D6B6B]">
                                            {pattern.name}
                                        </Heading>
                                        <SmallText style={{ color: pattern.color }} className="font-medium">
                                            {pattern.subtitle}
                                        </SmallText>
                                    </div>
                                    <div 
                                        className="w-10 h-10 rounded-full flex items-center justify-center shadow-soft-sm"
                                        style={{ backgroundColor: pattern.color + '25', boxShadow: `0 4px 16px -4px ${pattern.color}40` }}
                                    >
                                        <ChevronRight className="w-5 h-5" style={{ color: pattern.color }} />
                                    </div>
                                </div>
                                <BodyText className="text-[#7D8E95] text-sm mb-2">
                                    {pattern.description}
                                </BodyText>
                                <SmallText className="text-[#7D8E95]">
                                    Best for: {pattern.bestFor}
                                </SmallText>
                            </button>
                        ))}
                    </div>

                    {/* Link to full breathing patterns page */}
                    <div className="mt-6 text-center">
                        <button
                            onClick={() => navigate('/regulate/breathing')}
                            className="text-sm text-[#1D6B6B] hover:text-[#155555] transition-colors underline"
                        >
                            View all breathing patterns →
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    // Category picker screen
    if (showCategoryPicker) {
        return (
            <div className="min-h-screen bg-transparent">
                <header className="sticky top-0 z-50 glass-header">
                    <Container size="sm" className="py-5 flex items-center justify-between">
                        <IconButton 
                            onClick={() => navigate('/regulate')}
                            data-testid="back-btn"
                            className="text-[#7D8E95] hover:text-[#1D6B6B]"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </IconButton>
                        <Heading as="h1" size="text-lg">Choose Your Focus</Heading>
                        <div className="w-10" />
                    </Container>
                </header>

                <main className="max-w-xl mx-auto px-6 py-8">
                    <div className="text-center mb-8">
                        <SmallText className="text-[#7D8E95]">
                            What would help you most right now?
                        </SmallText>
                    </div>

                    <div className="space-y-3">
                        {/* Mixed option */}
                        <button
                            onClick={() => handleCategorySelect('mixed')}
                            className="w-full p-4 rounded-2xl glass-card hover:scale-[1.02] transition-all text-left"
                            data-testid="category-mixed"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FFBB98] to-[#1D6B6B] flex items-center justify-center flex-shrink-0">
                                    <Sparkles className="w-5 h-5 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <BodyText className="font-medium text-[#1D6B6B]">Surprise me</BodyText>
                                    <SmallText className="text-[#7D8E95]">A personalized mix</SmallText>
                                </div>
                                <ChevronRight className="w-4 h-4 text-[#7D8E95] flex-shrink-0" />
                            </div>
                        </button>

                        {/* EMDR/Bilateral Regulation - Special option */}
                        <button
                            onClick={() => handleCategorySelect('bilateral')}
                            className="w-full p-4 rounded-2xl glass-card-sage hover:scale-[1.02] transition-all text-left"
                            data-testid="category-bilateral"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1D6B6B] to-[#145555] flex items-center justify-center flex-shrink-0">
                                    <Eye className="w-5 h-5 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <BodyText className="font-medium text-[#1D6B6B]">{BILATERAL_CATEGORY.name}</BodyText>
                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#1D6B6B]/20 text-[#1D6B6B] flex-shrink-0">New</span>
                                    </div>
                                    <SmallText className="text-[#7D8E95]">{BILATERAL_CATEGORY.description}</SmallText>
                                </div>
                                <ChevronRight className="w-4 h-4 text-[#1D6B6B] flex-shrink-0" />
                            </div>
                        </button>

                        {categories.map((cat) => {
                            const Icon = CATEGORY_ICONS[cat.id] || Hand;
                            const colors = CATEGORY_COLORS[cat.id] || CATEGORY_COLORS.somatic;
                            return (
                                <button
                                    key={cat.id}
                                    onClick={() => handleCategorySelect(cat.id)}
                                    className={`w-full p-4 rounded-2xl ${colors.glass} hover:scale-[1.02] transition-all text-left`}
                                    data-testid={`category-${cat.id}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-xl ${colors.badge} flex items-center justify-center flex-shrink-0`}>
                                            <Icon className="w-5 h-5 text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <BodyText className={`font-medium ${colors.text}`}>{cat.name}</BodyText>
                                            <SmallText className="text-[#7D8E95]">{cat.description}</SmallText>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-[#7D8E95] flex-shrink-0" />
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </main>
            </div>
        );
    }

    // Completion screen
    if (showCompletion) {
        return (
            <div className="min-h-screen bg-transparent flex items-center justify-center px-6">
                <div className="max-w-sm w-full text-center">
                    <div className="w-20 h-20 rounded-full glass-card-sage flex items-center justify-center mx-auto mb-6 icon-glow-sage">
                        <Sparkles className="w-10 h-10 text-[#1D6B6B]" />
                    </div>
                    <Heading as="h2" size="text-2xl" className="mb-3">Nice work</Heading>
                    <BodyText className="text-[#7D8E95] mb-2">
                        Small resets add up. {actionCount > 1 ? `You did ${actionCount} resets.` : ''}
                    </BodyText>
                    {isPersonalized && (
                        <SmallText className="text-[#FFBB98] mb-6">
                            This session was personalized for you
                        </SmallText>
                    )}
                    <div className="space-y-3 mt-6">
                        <PrimaryButton 
                            onClick={handleReturnToRegulate} 
                            className="w-full" 
                            size="lg"
                            data-testid="return-to-regulate-btn"
                        >
                            Back to Regulation Tools
                        </PrimaryButton>
                        <SecondaryButton 
                            onClick={handleReturnHome} 
                            className="w-full" 
                            size="lg"
                            data-testid="return-home-btn"
                        >
                            Return Home
                        </SecondaryButton>
                        <button 
                            onClick={() => {
                                setShowCompletion(false);
                                setActionCount(0);
                                fetchPersonalizedSession(selectedFocus);
                            }} 
                            className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors mt-2"
                        >
                            Start another session
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Get current action's category info
    const { Icon: CategoryIcon, colors: categoryColors, categoryName } = getCategoryInfo(currentAction);

    return (
        <div className="min-h-screen bg-transparent">
            {/* Feedback Modal */}
            <ToolFeedback
                isOpen={showFeedback}
                onClose={handleFeedbackClose}
                toolId={currentAction?.id || 'micro-reset'}
                toolName={`Micro Reset - ${categoryName}`}
            />

            {/* Header - Glass-morphism */}
            <header className="sticky top-0 z-50 glass-header">
                <Container size="sm" className="py-5 flex items-center justify-between">
                    <IconButton 
                        onClick={() => navigate('/regulate')}
                        data-testid="back-btn"
                        className="text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-[#FFBB98]/40 transition-all duration-200"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <div className="text-center">
                        <Heading as="h1" size="text-lg">Micro-Reset</Heading>
                        {selectedFocus && selectedFocus !== 'mixed' && (
                            <SmallText className={`${categoryColors.text} text-xs`}>
                                {categoryName} focus
                            </SmallText>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <IconButton
                            onClick={() => setShowCategoryPicker(true)}
                            data-testid="change-focus-btn"
                            className="text-[#7D8E95] hover:text-[#1D6B6B]"
                            title="Change focus"
                        >
                            <Settings className="w-5 h-5" />
                        </IconButton>
                        <button
                            onClick={() => navigate('/home')}
                            className="hover:opacity-80 transition-opacity"
                            data-testid="header-logo"
                            aria-label="Go to dashboard"
                        >
                            <CoRegulateMark size={28} />
                        </button>
                    </div>
                </Container>
            </header>

            <main className="max-w-xl mx-auto px-6 py-8">
                <div className="flex flex-col min-h-[60vh]">
                    {/* Header area with category badge */}
                    <div className="text-center mb-6 brand-fade-in">
                        <IconContainer variant="blue" size="lg" useGradient={true} className="mx-auto mb-4">
                            <Zap className="w-6 h-6" />
                        </IconContainer>
                        <SmallText className="text-[#7D8E95]">
                            One small action. No thinking required.
                        </SmallText>
                        {isPersonalized && (
                            <SmallText className="text-[#FFBB98] text-xs mt-1">
                                Personalized for you
                            </SmallText>
                        )}
                    </div>

                    {/* [Main Tool Container — contains instruction, tool visual, and countdown ring] */}
                    <div className="flex-1 flex flex-col items-center justify-center w-full">
                        <div className="glass-card rounded-2xl p-5"
                            variant="elevated"
                            padding="xl"
                            className={`w-full rounded-3xl transition-all duration-200 ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
                            key={currentAction.id}
                        >
                            <div className="text-center py-4">
                                {/* Category badge */}
                                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${categoryColors.bg} mb-4`}>
                                    <CategoryIcon className={`w-4 h-4 ${categoryColors.text}`} />
                                    <span className={`text-xs font-medium ${categoryColors.text}`}>
                                        {categoryName}
                                    </span>
                                </div>

                                {/* Instruction text - inside container, above tool */}
                                <BodyText className="text-lg md:text-xl text-[#1D6B6B] leading-relaxed font-medium mb-6">
                                    {currentAction.action}
                                </BodyText>
                                
                                {/* Tool visual with integrated countdown */}
                                {currentAction.isBreath || currentAction.category === 'breath' ? (
                                    <div>
                                        <BoxBreathingVisual 
                                            size="md"
                                            variant="blue"
                                            autoStart={true}
                                            showControls={false}
                                            showPhaseIndicators={true}
                                        />
                                    </div>
                                ) : (
                                    /* Non-breath action with integrated countdown ring */
                                    <div className="relative inline-flex items-center justify-center">
                                        {/* Countdown ring as the visual indicator */}
                                        <div className="relative">
                                            <CountdownRing
                                                key={ringKey}
                                                duration={currentAction.duration || 10}
                                                size="lg"
                                                variant="teal"
                                                autoStart={true}
                                                showOnComplete={true}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Progress indicator */}
                                <div className="mt-4 flex justify-center gap-1">
                                    {sessionActions.slice(0, 6).map((_, idx) => (
                                        <div
                                            key={idx}
                                            className={`w-2 h-2 rounded-full transition-all ${
                                                idx < currentIndex ? 'bg-[#1D6B6B]' : 
                                                idx === currentIndex ? 'bg-[#FFBB98] scale-125' : 
                                                'bg-[#7D8E95]/30'
                                            }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ADHD Brain Science Micro-Insight (shown AFTER completing actions, optional) */}
                    {showInsight && currentInsight && (
                        <div className="mb-4">
                            <MicroInsight 
                                insight={currentInsight}
                                variant="default"
                                onDismiss={handleDismissInsight}
                            />
                        </div>
                    )}

                    {/* Gentle encouragement */}
                    <div className="text-center my-6">
                        <SmallText className="text-[#7D8E95]">
                            Do as many or as few as you need.
                        </SmallText>
                    </div>

                    {/* Bottom spacer - extended for two-button bar */}
                    <BottomBarSpacer height="extended" />
                </div>
            </main>

            {/* Persistent Bottom Action Zone - compact with smaller buttons */}
            <PersistentBottomBar padding="compact">
                <div className="flex gap-3">
                    <SecondaryButton
                        onClick={handleDone}
                        className="flex-1"
                        size="action"
                        data-testid="done-btn"
                    >
                        <Check className="w-4 h-4 mr-1.5" />
                        Done
                    </SecondaryButton>
                    <PrimaryButton
                        onClick={handleNext}
                        className="flex-1"
                        size="action"
                        data-testid="next-btn"
                    >
                        <RotateCcw className="w-4 h-4 mr-1.5" />
                        Another one
                    </PrimaryButton>
                </div>
            </PersistentBottomBar>
        </div>
    );
};

export default MicroReset;
